/**
 * buildStructure - Converts MolecularWorkspace to ARIA tree structure
 *
 * Creates a hierarchical structure suitable for screen reader navigation.
 * Identifies connected components (molecules), groups atoms/bonds by features,
 * and creates nested lone pair nodes.
 *
 * @package @alchemiesolns/svg-interactive
 */

import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';
import { nameMolecule } from '@alchemiesolns/feature-extractor';
import type {
  SVGStructure,
  SVGStructureTree,
  StructureIndex,
  MoleculeNode,
  AtomLeaf,
  BondLeaf,
  BondEndpointLeaf,
  LonePairLeaf,
  FeatureNode,
  ArrowLeaf,
} from './types';
import { serializeFeatures, type SerializationResult } from '@alchemiesolns/feature-extractor';
import { describeArrow } from './arrowDescriptionTemplate';

// Import from modular utility files
import { isHydrogen } from './atomUtils';
import { createBondLeafForAtom } from './bondUtils';
import { createAtomLeaf } from './atomFactory';
import { buildFeatureNodes } from './featureUtils';
import { findConnectedComponents } from './graphUtils';
import { buildWorkspaceSummary, moleculeFormula } from './workspaceSummaryTemplate';

// Re-export navigation utilities for backward compatibility
export type { TreeNavigation } from './navigationUtils';
export {
  getTreeNodeIds,
  getNodeById,
  buildTreeNavigation,
  treeNavHelpers,
  getDescendantIds,
} from './navigationUtils';

/**
 * Build the ARIA tree structure from a MolecularWorkspace
 *
 * @param workspace - The molecular workspace to convert
 * @param featureResult - Optional extracted features from feature-extractor-compat
 * @returns SVGStructure with tree and index for navigation
 */
export function buildStructure(
  workspace: MolecularWorkspace,
  featureResult?: WorkspaceFeatureResult
): SVGStructure {
  // Initialize index maps
  const atomsById = new Map<string, AtomLeaf>();
  const bondsById = new Map<string, BondLeaf>();
  const endpointsById = new Map<string, BondEndpointLeaf>();
  const lonePairsById = new Map<string, LonePairLeaf>();
  const moleculesById = new Map<string, MoleculeNode>();
  const featuresById = new Map<string, FeatureNode>();
  const parentById = new Map<string, string>();

  // Find connected components
  const components = findConnectedComponents(workspace);

  // PIPH-309 ordering: serialize the feature graph once up front so the tree
  // builder can read SerializedFeature.atomIndices (IUPAC-reordered) when
  // ordering children inside ring / chain feature subtrees. The same result
  // is reused by the arrow-description block further down.
  const serialized: SerializationResult | undefined = featureResult
    ? serializeFeatures(
        featureResult.features.map(wf => wf.feature),
        featureResult.graph,
      )
    : undefined;

  // Build molecule nodes
  const molecules: MoleculeNode[] = components.map((component, index) => {
    const moleculeId = `molecule-${index + 1}`;
    const moleculeAtomIds = new Set(component.atomIds);
    const moleculeBondIds = new Set(component.bondIds);

    let features: FeatureNode[] = [];
    let claimedAtomIds = new Set<string>();

    // Build features if feature extraction results are provided
    if (featureResult) {
      const result = buildFeatureNodes(
        featureResult.features,
        workspace,
        moleculeAtomIds,
        moleculeBondIds,
        atomsById,
        bondsById,
        endpointsById,
        lonePairsById,
        featuresById,
        parentById,
        moleculeId,
        // PIPH-118: Pass graph and indexToAtomId for bridged bicyclic atom ordering
        featureResult.graph,
        featureResult.indexToAtomId,
        // PIPH-309 ordering: pass the serialization result so chain / ring atom
        // ordering inside FeatureNode.atoms follows IUPAC numbering.
        serialized,
      );
      features = result.features;
      claimedAtomIds = result.claimedAtomIds;
      // Note: claimedBondIds no longer used since bonds are children of atoms
    }

    // Create atom leaves for unclaimed atoms (skip hydrogens - they're added as children)
    // Unclaimed atoms use their original atomId as the tree ID (no feature prefix)
    //
    // PIPH-318: enumeration of same-element unclaimed atoms ("Chlorine-1" …) plus
    // the PIPH-310 prose charge prefix is now done at the single source of truth —
    // the feature-extractor resolver (molecule-scoped index in serializeFeatures)
    // surfaced through buildAltTextMap. The HTML tree reads that map entry, not
    // this leaf.label, so the leaf carries only a plain fallback name.
    const unclaimedAtomLeaves: AtomLeaf[] = [];
    for (const atomId of component.atomIds) {
      if (claimedAtomIds.has(atomId)) continue;
      // Skip hydrogens - they'll be added as children of their parent atoms
      if (isHydrogen(workspace, atomId)) continue;

      const atom = workspace.getAtom(atomId)!;

      const leaf = createAtomLeaf(
        atomId,
        atomId,  // Unclaimed atoms use atomId as tree ID (no feature scope)
        atom.symbol,
        atom.charge,
        atom.lonePairs || 0,
        lonePairsById,
        bondsById,
        endpointsById,
        parentById,
        moleculeId,
        workspace,
        atomsById,
        undefined,
        undefined,
      );

      unclaimedAtomLeaves.push(leaf);
      atomsById.set(atomId, leaf);
    }

    // Populate bondNodes for unclaimed atoms (bonds to other non-H atoms)
    for (const atomLeaf of unclaimedAtomLeaves) {
      const atomId = atomLeaf.atomId;

      for (const bondId of component.bondIds) {
        const bond = workspace.getBond(bondId);
        if (!bond) continue;

        // Skip bonds involving hydrogens - these are parent-child relationships
        if (bond.a1.symbol === 'H' || bond.a2.symbol === 'H') continue;

        // Check if this bond involves the current atom
        let otherAtomId: string | null = null;
        if (bond.a1.id === atomId) {
          otherAtomId = bond.a2.id;
        } else if (bond.a2.id === atomId) {
          otherAtomId = bond.a1.id;
        }

        if (!otherAtomId) continue;

        // Include ALL bonds for heavy atoms, including those to atoms in features
        // This ensures connection bonds are navigable from unclaimed atoms
        const otherAtom = workspace.getAtom(otherAtomId);
        if (!otherAtom) continue;

        // Create bond leaf as child of this atom
        // Unclaimed atoms don't have numbered labels, so pass undefined for all index params
        const bondLeaf = createBondLeafForAtom(
          bondId,
          atomId,
          otherAtomId,
          atomLeaf.symbol,
          otherAtom.symbol,
          bond.order,
          bond.bondType,
          atomLeaf.id,  // treeIdPrefix
          bondsById,
          endpointsById,
          parentById,
          atomLeaf.id,  // parentTreeId
          undefined,    // parent: no index for unclaimed atoms
          undefined,    // parent: no total for unclaimed atoms
          undefined,    // other: no index for unclaimed atoms
          undefined,    // other: no total for unclaimed atoms
          atomLeaf.x,   // PIPH-74: parent atom X position
          atomLeaf.y,   // PIPH-74: parent atom Y position
          otherAtom.x,  // PIPH-74: other atom X position
          otherAtom.y,  // PIPH-74: other atom Y position
          atomLeaf.hasStereoContext  // PIPH-74: include direction in label
        );

        atomLeaf.bondNodes.push(bondLeaf);
      }
    }

    // PIPH-324: Molecule label is the complete chemical formula (implicit H +
    // net charge), matching the workspace-overview spoken summary. Reuse the
    // single source of truth `moleculeFormula`. Fall back to the atom/bond
    // count name only when feature extraction is unavailable, or the formula
    // is empty.
    let moleculeLabel: string;
    if (featureResult) {
      const formula = moleculeFormula(component, workspace, featureResult);
      moleculeLabel = formula
        ? `Molecule ${index + 1}: ${formula}`
        : nameMolecule(component.atomIds.length, component.bondIds.length, index);
    } else {
      moleculeLabel = nameMolecule(component.atomIds.length, component.bondIds.length, index);
    }

    const molecule: MoleculeNode = {
      type: 'molecule',
      id: moleculeId,
      label: moleculeLabel,
      features,
      unclaimedAtoms: unclaimedAtomLeaves,
    };

    moleculesById.set(moleculeId, molecule);
    return molecule;
  });

  // Per-feature atom labels are produced by buildFeatureNodes during tree
  // construction — each AtomLeaf now carries its feature's scoped label.
  // Workspace-summary lines that need feature-agnostic naming use the
  // default-feature resolver from feature-extractor (see workspaceSummaryTemplate).

  // Create tree structure
  const totalAtoms = workspace.getAtomCount();
  const totalBonds = workspace.getBondCount();
  const moleculeCount = molecules.length;

  let treeLabel = 'Chemical structure';
  if (moleculeCount === 0) {
    treeLabel = 'Empty chemical structure';
  } else if (moleculeCount === 1) {
    treeLabel = `Chemical structure with ${totalAtoms} ${totalAtoms === 1 ? 'atom' : 'atoms'}`;
  } else {
    treeLabel = `Chemical structure with ${moleculeCount} molecules, ${totalAtoms} total atoms`;
  }

  // Build arrow nodes if there are arrows and featureResult is available
  const arrowsById = new Map<string, ArrowLeaf>();
  let arrowLeaves: ArrowLeaf[] | undefined;
  const workspaceArrows = workspace.getArrows();

  if (workspaceArrows.length > 0 && featureResult && serialized) {
    // Build atom ID to index mapping
    const atomIdToIndex = new Map<string, number>();
    for (let i = 0; i < featureResult.indexToAtomId.length; i++) {
      atomIdToIndex.set(featureResult.indexToAtomId[i], i);
    }

    // Generate arrow leaves
    arrowLeaves = workspaceArrows.map((arrow, index) => {
      const arrowId = `arrow-${index + 1}`;
      const description = describeArrow(arrow, workspace, serialized, atomIdToIndex);

      const arrowLeaf: ArrowLeaf = {
        type: 'arrow',
        id: arrowId,
        label: `Arrow ${index + 1}`,
        description,
        arrowType: arrow.type,
        sourceId: arrow.srcId,
        targetId: arrow.dstId,
        anchorId: arrow.anchorId,
      };

      arrowsById.set(arrowId, arrowLeaf);
      return arrowLeaf;
    });
  }

  // PIPH-87: Use workspace summary template for description.
  // buildWorkspaceSummary builds its own per-molecule AtomLabelResolver from
  // each molecule's serialization — no precomputed map needed.
  const description = featureResult
    ? buildWorkspaceSummary(workspace, featureResult, undefined)
    : `Contains ${moleculeCount} ${moleculeCount === 1 ? 'molecule' : 'molecules'}, ${totalAtoms} atoms, and ${totalBonds} bonds.`;

  const tree: SVGStructureTree = {
    label: treeLabel,
    description,
    molecules,
    arrows: arrowLeaves,
  };

  const structureIndex: StructureIndex = {
    atomsById,
    bondsById,
    endpointsById,
    lonePairsById,
    moleculesById,
    featuresById,
    arrowsById,
    parentById,
  };

  return { tree, index: structureIndex };
}
